import { useEffect, useMemo, useState } from "react";
import { api } from "./adminApi";

/* ---------- Responsive helper ---------- */
function useMediaQuery(query) {
  const [matches, setMatches] = useState(() => window.matchMedia(query).matches);
  useEffect(() => {
    const m = window.matchMedia(query);
    const onChange = () => setMatches(m.matches);
    m.addEventListener ? m.addEventListener("change", onChange) : m.addListener(onChange);
    return () => (m.removeEventListener ? m.removeEventListener("change", onChange) : m.removeListener(onChange));
  }, [query]);
  return matches;
}

export default function AdminContributions() {
  const isMobile = useMediaQuery("(max-width: 920px)");

  const [persons, setPersons] = useState([]);
  const [personId, setPersonId] = useState("");
  const [status, setStatus] = useState("pending");
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  // Pagination (client-side)
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  // Media preview modal
  const [mediaOpen, setMediaOpen] = useState(false);
  const [selected, setSelected] = useState(null);

  // Full text drawer (right-side popup)
  const [textOpen, setTextOpen] = useState(false);
  const [textSelected, setTextSelected] = useState(null);

  async function loadPersons() {
    try {
      const res = await api.get("/api/admin/persons");
      const list = res.data || [];
      setPersons(list);
      if (!personId && list.length) setPersonId(String(list[0].id));
    } catch {
      setPersons([]);
    }
  }

  async function loadContributions() {
    setErr("");

    if (!personId) {
      setRows([]);
      setErr("Please select a profile/person first.");
      return;
    }

    setLoading(true);
    try {
      const url =
        `/api/admin/contributions?personId=${encodeURIComponent(personId)}` +
        `&status=${encodeURIComponent(status)}` +
        `&q=${encodeURIComponent(q || "")}`;

      const res = await api.get(url);
      setRows(res.data || []);
    } catch (e) {
      setErr(e?.response?.data?.message || "Failed to load contributions");
      setRows([]);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadPersons();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setPage(1);
  }, [personId, status, pageSize, q]);

  useEffect(() => {
    loadContributions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [personId, status]);

  function openMedia(contribution) {
    setSelected(contribution);
    setMediaOpen(true);
  }

  function closeMedia() {
    setMediaOpen(false);
    setSelected(null);
  }

  function openText(contribution) {
    setTextSelected(contribution);
    setTextOpen(true);
  }

  function closeText() {
    setTextOpen(false);
    setTextSelected(null);
  }

  async function approve(id) {
    try {
      await api.post(`/api/admin/contributions/${id}/approve`);
      await loadContributions();
    } catch (e) {
      setErr(e?.response?.data?.message || "Failed to approve");
    }
  }

  async function reject(id) {
    try {
      await api.post(`/api/admin/contributions/${id}/reject`);
      await loadContributions();
    } catch (e) {
      setErr(e?.response?.data?.message || "Failed to reject");
    }
  }

  // ✅ NEW: delete single contribution
  async function deleteOne(id) {
    const ok = window.confirm("Delete this contribution permanently?");
    if (!ok) return;

    try {
      setErr("");
      await api.delete(`/api/admin/contributions/${id}`);
      await loadContributions();
    } catch (e) {
      setErr(e?.response?.data?.message || "Failed to delete");
    }
  }

  // ✅ NEW: delete all (filtered by personId + status + q)
  async function deleteAllFiltered() {
    if (!personId) {
      setErr("Please select a profile/person first.");
      return;
    }

    const msg =
      `Delete ALL contributions for this person with current filters?\n\n` +
      `Person: ${personId}\nStatus: ${status}\nSearch: ${q || "-"}\n\n` +
      `This cannot be undone.`;

    const ok = window.confirm(msg);
    if (!ok) return;

    try {
      setErr("");
      const url =
        `/api/admin/contributions?personId=${encodeURIComponent(personId)}` +
        `&status=${encodeURIComponent(status)}` +
        `&q=${encodeURIComponent(q || "")}`;

      await api.delete(url);
      await loadContributions();
    } catch (e) {
      setErr(e?.response?.data?.message || "Failed to delete all");
    }
  }

  const filtered = useMemo(() => {
    const s = (q || "").trim().toLowerCase();
    if (!s) return rows || [];
    return (rows || []).filter(
      (r) =>
        (r.full_name || "").toLowerCase().includes(s) ||
        (r.mobile || "").toLowerCase().includes(s) ||
        (r.contribution_text || "").toLowerCase().includes(s)
    );
  }, [rows, q]);

  const pageCount = useMemo(() => Math.max(1, Math.ceil(filtered.length / pageSize)), [filtered.length, pageSize]);
  const paged = useMemo(() => {
    const start = (page - 1) * pageSize;
    return (filtered || []).slice(start, start + pageSize);
  }, [filtered, page, pageSize]);

  useEffect(() => {
    if (page > pageCount) setPage(pageCount);
  }, [page, pageCount]);

  return (
    <div style={styles.page}>
      <div style={styles.header}>
        <div style={styles.title}>Moderate Contributions</div>
        <div style={styles.subtitle}>Review public submissions, preview attached media, and approve/reject content.</div>
      </div>

      <div style={styles.filtersCard}>
        <div style={styles.filtersRow}>
          <div style={styles.filterGroup}>
            <div style={styles.label}>PROFILE</div>
            <select value={personId} onChange={(e) => setPersonId(e.target.value)} style={styles.select}>
              {persons.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name_en || p.name_kn || `Person ${p.id}`} ({p.slug || p.name_en || p.name_kn || ""})
                </option>
              ))}
            </select>
          </div>

          <div style={styles.filterGroup}>
            <div style={styles.label}>STATUS</div>
            <div style={styles.segWrap}>
              <button
                type="button"
                style={{ ...styles.segBtn, ...(status === "pending" ? styles.segActive : null) }}
                onClick={() => setStatus("pending")}
              >
                Pending
              </button>
              <button
                type="button"
                style={{ ...styles.segBtn, ...(status === "approved" ? styles.segActiveGreen : null) }}
                onClick={() => setStatus("approved")}
              >
                Approved
              </button>
              <button
                type="button"
                style={{ ...styles.segBtn, ...(status === "rejected" ? styles.segActiveRed : null) }}
                onClick={() => setStatus("rejected")}
              >
                Rejected
              </button>
            </div>
          </div>
        </div>

        <div style={styles.searchRow}>
          <div style={styles.searchWrap}>
            <span style={styles.searchIcon}>⌕</span>
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search contributor / mobile / text..."
              style={styles.search}
            />
          </div>

          <button type="button" style={styles.primaryBtn} onClick={loadContributions}>
            Search
          </button>

          {/* ✅ NEW: Delete All (Filtered) */}
          <button type="button" style={styles.dangerBtn} onClick={deleteAllFiltered}>
            Delete All (Filtered)
          </button>
        </div>

        {err ? <div style={styles.error}>{err}</div> : null}
      </div>

      <div style={styles.card}>
        <div style={styles.cardHeader}>
          <div style={styles.cardTitle}>Results</div>
          <div style={styles.cardMeta}>
            {loading ? "Loading…" : `${filtered.length} record(s)`} • Showing{" "}
            {filtered.length ? `${(page - 1) * pageSize + 1}-${Math.min(page * pageSize, filtered.length)}` : "0"}
          </div>
        </div>

        {isMobile ? (
          <div style={styles.mobileList}>
            {paged.map((c) => (
              <div key={c.id} style={styles.mobileItem}>
                <div style={styles.mobileTop}>
                  <div style={styles.personCell}>
                    <Avatar name={c.full_name || "-"} />
                    <div style={{ minWidth: 0 }}>
                      <div style={styles.name}>{c.full_name || "-"}</div>
                      <div style={styles.sub}>{c.mobile || "-"}</div>
                    </div>
                  </div>
                  <StatusPill status={c.status} />
                </div>

                <div style={styles.mobileBody}>
                  <div
                    style={{ ...styles.bodyTextClamp, cursor: "pointer" }}
                    role="button"
                    tabIndex={0}
                    title="Click to read full text"
                    onClick={() => openText(c)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        openText(c);
                      }
                    }}
                  >
                    {c.contribution_text || "-"}
                  </div>

                  <div style={styles.mobileMetaRow}>
                    <span style={styles.mono}>{c.content_lang || "-"}</span>
                    <span style={styles.sub}>{c.created_at ? new Date(c.created_at).toLocaleString() : "-"}</span>
                  </div>

                  <div style={styles.mobileMetaRow}>
                    {c.media && c.media.length ? (
                      <button style={styles.mediaBtn} onClick={() => openMedia(c)} type="button">
                        View Media ({c.media.length})
                      </button>
                    ) : (
                      <span style={styles.sub}>No media</span>
                    )}
                  </div>

                  {/* ✅ Actions: keep approve/reject behavior; add delete always */}
                  <div style={styles.mobileActions}>
                    {c.status === "pending" ? (
                      <>
                        <button style={{ ...styles.approveBtn, width: "100%" }} onClick={() => approve(c.id)} type="button">
                          ✓ Approve
                        </button>
                        <button style={{ ...styles.rejectBtn, width: "100%" }} onClick={() => reject(c.id)} type="button">
                          ✕ Reject
                        </button>
                      </>
                    ) : null}

                    <button style={{ ...styles.deleteBtn, width: "100%" }} onClick={() => deleteOne(c.id)} type="button">
                      🗑 Delete
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div style={styles.tableWrap}>
            <table style={styles.table}>
              <thead>
                <tr>
                  <Th>Contributor</Th>
                  <Th>Text</Th>
                  <Th>Lang</Th>
                  <Th>Status</Th>
                  <Th>Date</Th>
                  <Th>Media</Th>
                  <Th>Actions</Th>
                </tr>
              </thead>

              <tbody>
                {paged.map((c) => (
                  <tr key={c.id} style={styles.tr}>
                    <Td>
                      <div style={styles.personCell}>
                        <Avatar name={c.full_name || "-"} />
                        <div style={{ minWidth: 0 }}>
                          <div style={styles.name}>{c.full_name || "-"}</div>
                          <div style={styles.sub}>{c.mobile || "-"}</div>
                        </div>
                      </div>
                    </Td>

                    <Td>
                      <div
                        style={{ ...styles.bodyTextClamp, cursor: "pointer" }}
                        role="button"
                        tabIndex={0}
                        title="Click to read full text"
                        onClick={() => openText(c)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            openText(c);
                          }
                        }}
                      >
                        {c.contribution_text || "-"}
                      </div>
                    </Td>

                    <Td>
                      <span style={styles.mono}>{c.content_lang || "-"}</span>
                    </Td>

                    <Td>
                      <StatusPill status={c.status} />
                    </Td>

                    <Td>
                      <div style={styles.sub}>{c.created_at ? new Date(c.created_at).toLocaleString() : "-"}</div>
                    </Td>

                    <Td>
                      {c.media && c.media.length ? (
                        <button style={styles.mediaBtn} onClick={() => openMedia(c)} type="button">
                          View ({c.media.length})
                        </button>
                      ) : (
                        <span style={styles.sub}>—</span>
                      )}
                    </Td>

                    <Td>
                      <div style={styles.actions}>
                        {c.status === "pending" ? (
                          <>
                            <button style={styles.approveBtn} onClick={() => approve(c.id)} type="button">
                              ✓ Approve
                            </button>
                            <button style={styles.rejectBtn} onClick={() => reject(c.id)} type="button">
                              ✕ Reject
                            </button>
                          </>
                        ) : (
                          <span style={styles.sub}>—</span>
                        )}

                        {/* ✅ NEW: Delete always available */}
                        <button style={styles.deleteBtn} onClick={() => deleteOne(c.id)} type="button">
                          🗑 Delete
                        </button>
                      </div>
                    </Td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {!loading ? (
          <PaginationBar
            page={page}
            pageSize={pageSize}
            total={filtered.length}
            pageCount={pageCount}
            onPageChange={setPage}
            onPageSizeChange={(n) => {
              setPageSize(n);
              setPage(1);
            }}
            compact={isMobile}
          />
        ) : null}

        <TextDrawer open={textOpen} onClose={closeText} contribution={textSelected} />
        <MediaModal open={mediaOpen} onClose={closeMedia} contribution={selected} />
      </div>
    </div>
  );
}

/* ---------- UI Components ---------- */

function PaginationBar({ page, pageSize, total, pageCount, onPageChange, onPageSizeChange, compact }) {
  const canPrev = page > 1;
  const canNext = page < pageCount;

  return (
    <div style={styles.pager}>
      <div style={styles.pagerLeft}>
        <button
          type="button"
          style={{ ...styles.pagerBtn, ...(canPrev ? null : styles.pagerBtnDisabled) }}
          onClick={() => canPrev && onPageChange(page - 1)}
          disabled={!canPrev}
        >
          ← Prev
        </button>
        <button
          type="button"
          style={{ ...styles.pagerBtn, ...(canNext ? null : styles.pagerBtnDisabled) }}
          onClick={() => canNext && onPageChange(page + 1)}
          disabled={!canNext}
        >
          Next →
        </button>
      </div>

      <div style={styles.pagerCenter}>
        <span style={styles.sub}>
          Page <b>{page}</b> of <b>{pageCount}</b>
          {!compact ? (
            <>
              {" "}
              • Total <b>{total}</b>
            </>
          ) : null}
        </span>
      </div>

      <div style={styles.pagerRight}>
        <select value={pageSize} onChange={(e) => onPageSizeChange(Number(e.target.value))} style={styles.pagerSelect}>
          {[10, 20, 50, 100].map((n) => (
            <option key={n} value={n}>
              {n}/page
            </option>
          ))}
        </select>
      </div>
    </div>
  );
}

function Th({ children }) {
  return <th style={styles.th}>{children}</th>;
}

function Td({ children }) {
  return <td style={styles.td}>{children}</td>;
}

function Avatar({ name }) {
  const initial = (name || "?").trim().slice(0, 1).toUpperCase();
  return <div style={styles.avatar}>{initial}</div>;
}

function StatusPill({ status }) {
  const s = String(status || "").toLowerCase();
  const style =
    s === "approved" ? styles.pillApproved : s === "rejected" ? styles.pillRejected : styles.pillPending;

  return <span style={{ ...styles.pill, ...style }}>{status || "-"}</span>;
}

function TextDrawer({ open, onClose, contribution }) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open || !contribution) return null;

  return (
    <div style={styles.drawerOverlay} onClick={onClose} role="dialog" aria-modal="true">
      <div style={styles.drawerPanel} onClick={(e) => e.stopPropagation()}>
        <div style={styles.drawerHeader}>
          <div>
            <div style={styles.drawerTitle}>Full Contribution</div>
            <div style={styles.drawerMeta}>
              <b>{contribution.full_name || "-"}</b>
              {" • "}
              {contribution.mobile || "-"}
              {" • "}
              {contribution.created_at ? new Date(contribution.created_at).toLocaleString() : "-"}
            </div>
          </div>

          <button type="button" style={styles.drawerCloseBtn} onClick={onClose} aria-label="Close">
            ✕
          </button>
        </div>

        <div style={styles.drawerBody}>
          <div style={styles.drawerBadges}>
            <span style={styles.drawerBadge}>{contribution.content_lang || "-"}</span>
            <span style={styles.drawerBadge}>{contribution.status || "-"}</span>
          </div>

          <div style={styles.drawerText}>{contribution.contribution_text || "-"}</div>
        </div>
      </div>
    </div>
  );
}

function MediaModal({ open, onClose, contribution }) {
  if (!open || !contribution) return null;

  const items = contribution.media || [];
  return (
    <div style={styles.modalOverlay} onClick={onClose} role="dialog" aria-modal="true">
      <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div style={styles.modalHeader}>
          <div style={styles.modalTitle}>Attached Media</div>
          <button type="button" style={styles.modalClose} onClick={onClose}>
            ✕
          </button>
        </div>

        <div style={styles.modalBody}>
          {items.length ? (
            <div style={styles.mediaGrid}>
              {items.map((m, idx) => (
                <MediaThumb key={idx} media={m} />
              ))}
            </div>
          ) : (
            <div style={styles.sub}>No media found</div>
          )}
        </div>
      </div>
    </div>
  );
}

function MediaThumb({ media }) {
  const kind = (media?.media_type || media?.type || "").toLowerCase();
  const url = media?.url || media?.media_url || media?.path || "";
  const safeUrl = url.startsWith("http") ? url : url.startsWith("/") ? url : `/${url}`;

  if (kind === "image") {
    return (
      <a style={styles.thumb} href={safeUrl} target="_blank" rel="noreferrer">
        <img src={safeUrl} alt="" style={styles.thumbImg} />
      </a>
    );
  }

  if (kind === "video") {
    return (
      <div style={styles.thumb}>
        <video controls style={styles.thumbVideo}>
          <source src={safeUrl} />
        </video>
      </div>
    );
  }

  if (kind === "audio") {
    return (
      <div style={styles.thumb}>
        <audio controls style={styles.thumbAudio}>
          <source src={safeUrl} />
        </audio>
      </div>
    );
  }

  return (
    <a style={styles.thumbFile} href={safeUrl} target="_blank" rel="noreferrer">
      Download File
    </a>
  );
}

const styles = {
  page: { padding: 18 },

  header: { marginBottom: 16 },
  title: { fontSize: 34, fontWeight: 1000, color: "#0b1220", letterSpacing: "-0.8px", lineHeight: 1.05 },
  subtitle: { marginTop: 6, fontSize: 14, fontWeight: 800, color: "#667085" },

  filtersCard: {
    background: "linear-gradient(180deg,#ffffff,#fbfdff)",
    border: "1px solid #e8eef6",
    borderRadius: 18,
    padding: 14,
    boxShadow: "0 20px 45px rgba(16,24,40,0.06)",
    marginBottom: 16,
  },

  filtersRow: {
    display: "flex",
    gap: 14,
    flexWrap: "wrap",
    alignItems: "flex-end",
    justifyContent: "space-between",
  },

  filterGroup: { minWidth: 260, flex: "1 1 320px" },
  label: { fontSize: 12, fontWeight: 950, color: "#667085", letterSpacing: "0.08em" },

  select: {
    marginTop: 8,
    width: "100%",
    height: 44,
    borderRadius: 14,
    border: "1px solid #e5e7eb",
    padding: "0 12px",
    fontWeight: 900,
    outline: "none",
    background: "#fff",
  },

  segWrap: { marginTop: 8, display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" },

  segBtn: {
    height: 40,
    padding: "0 14px",
    borderRadius: 999,
    border: "1px solid #e5e7eb",
    background: "#fff",
    cursor: "pointer",
    fontWeight: 950,
    color: "#0b1220",
  },

  segActive: {
    background: "linear-gradient(135deg,#3b82f6,#2563eb)",
    border: 0,
    color: "#fff",
    boxShadow: "0 12px 24px rgba(37,99,235,0.16)",
  },
  segActiveGreen: {
    background: "linear-gradient(135deg,#10b981,#34d399)",
    border: 0,
    color: "#fff",
    boxShadow: "0 12px 24px rgba(16,185,129,0.16)",
  },
  segActiveRed: {
    background: "linear-gradient(135deg,#ef4444,#f97316)",
    border: 0,
    color: "#fff",
    boxShadow: "0 12px 24px rgba(239,68,68,0.16)",
  },

  searchRow: { marginTop: 12, display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" },

  searchWrap: {
    flex: "1 1 420px",
    display: "flex",
    alignItems: "center",
    gap: 8,
    border: "1px solid #e5e7eb",
    borderRadius: 14,
    background: "#fff",
    padding: "0 12px",
    height: 44,
  },

  searchIcon: { fontWeight: 950, color: "#98a2b3" },

  search: { border: 0, outline: "none", width: "100%", fontWeight: 850, fontSize: 14, color: "#0b1220" },

  primaryBtn: {
    height: 44,
    padding: "0 18px",
    borderRadius: 14,
    border: 0,
    cursor: "pointer",
    fontWeight: 950,
    color: "#fff",
    background: "linear-gradient(135deg,#2563eb,#60a5fa)",
    boxShadow: "0 14px 30px rgba(37,99,235,0.18)",
    minWidth: 120,
  },

  // ✅ NEW: Delete All button
  dangerBtn: {
    height: 44,
    padding: "0 18px",
    borderRadius: 14,
    border: 0,
    cursor: "pointer",
    fontWeight: 950,
    color: "#fff",
    background: "#b42318",
    boxShadow: "0 14px 30px rgba(180,35,24,0.18)",
    minWidth: 170,
  },

  error: {
    marginTop: 10,
    padding: 10,
    borderRadius: 14,
    border: "1px solid rgba(239,68,68,0.25)",
    background: "rgba(239,68,68,0.08)",
    color: "#b42318",
    fontWeight: 900,
  },

  card: {
    background: "linear-gradient(180deg,#ffffff,#fbfdff)",
    border: "1px solid #e8eef6",
    borderRadius: 18,
    padding: 14,
    boxShadow: "0 20px 45px rgba(16,24,40,0.06)",
  },

  cardHeader: { display: "flex", alignItems: "flex-end", justifyContent: "space-between", gap: 10, flexWrap: "wrap" },
  cardTitle: { fontSize: 18, fontWeight: 1000, color: "#0b1220" },
  cardMeta: { fontSize: 12, fontWeight: 900, color: "#667085" },

  tableWrap: { marginTop: 10, overflow: "auto" },
  table: { width: "100%", borderCollapse: "separate", borderSpacing: 0, marginTop: 12 },
  th: {
    textAlign: "left",
    fontSize: 12,
    fontWeight: 950,
    color: "#667085",
    letterSpacing: "0.08em",
    padding: "12px 10px",
    borderBottom: "1px solid #eef2f7",
    whiteSpace: "nowrap",
  },
  td: { padding: "14px 10px", borderBottom: "1px solid #f1f5f9", verticalAlign: "top", minWidth: 90 },
  tr: { background: "#fff" },

  personCell: { display: "flex", alignItems: "center", gap: 10, minWidth: 0 },
  avatar: {
    height: 42,
    width: 42,
    borderRadius: 999,
    display: "grid",
    placeItems: "center",
    background: "linear-gradient(135deg,#e0e7ff,#cffafe)",
    border: "1px solid #eef2f7",
    fontWeight: 950,
    color: "#0b1220",
  },

  name: {
    fontWeight: 950,
    color: "#0b1220",
    lineHeight: 1.2,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  sub: { fontSize: 12, fontWeight: 850, color: "#667085" },
  mono: {
    fontFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono', 'Courier New', monospace",
    fontSize: 12,
    fontWeight: 900,
    color: "#0b1220",
  },

  bodyTextClamp: {
    fontSize: 13,
    fontWeight: 850,
    color: "#111827",
    lineHeight: 1.35,
    display: "-webkit-box",
    WebkitLineClamp: 3,
    WebkitBoxOrient: "vertical",
    overflow: "hidden",
  },

  drawerOverlay: {
    position: "fixed",
    inset: 0,
    background: "rgba(11,18,32,0.45)",
    zIndex: 9999,
    display: "flex",
    justifyContent: "flex-end",
  },

  drawerPanel: {
    width: "520px",
    maxWidth: "92vw",
    height: "100vh",
    background: "#fff",
    borderLeft: "1px solid #eef2f7",
    boxShadow: "-16px 0 40px rgba(16,24,40,0.18)",
    display: "flex",
    flexDirection: "column",
  },

  drawerHeader: {
    padding: "14px 14px",
    borderBottom: "1px solid #eef2f7",
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: 12,
    background: "linear-gradient(180deg,#ffffff,#fbfdff)",
  },

  drawerTitle: { fontSize: 16, fontWeight: 950, color: "#0b1220" },
  drawerMeta: { marginTop: 4, fontSize: 12, fontWeight: 850, color: "#667085", lineHeight: 1.3 },

  drawerCloseBtn: {
    height: 36,
    width: 36,
    borderRadius: 12,
    border: "1px solid #e5e7eb",
    background: "#fff",
    cursor: "pointer",
    fontWeight: 950,
    color: "#0b1220",
  },

  drawerBody: { padding: 14, overflow: "auto" },

  drawerBadges: { display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 10 },

  drawerBadge: {
    height: 28,
    display: "inline-flex",
    alignItems: "center",
    padding: "0 10px",
    borderRadius: 999,
    border: "1px solid #e5e7eb",
    background: "#fff",
    fontSize: 12,
    fontWeight: 900,
    color: "#0b1220",
  },

  drawerText: {
    whiteSpace: "pre-wrap",
    wordBreak: "break-word",
    fontSize: 14,
    fontWeight: 850,
    color: "#111827",
    lineHeight: 1.5,
    border: "1px solid #eef2f7",
    borderRadius: 14,
    padding: 12,
    background: "linear-gradient(180deg,#ffffff,#fafbff)",
  },

  pill: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    height: 30,
    padding: "0 12px",
    borderRadius: 999,
    border: "1px solid #e5e7eb",
    fontSize: 12,
    fontWeight: 950,
    textTransform: "capitalize",
  },
  pillPending: { background: "#fff7ed", borderColor: "#fed7aa", color: "#9a3412" },
  pillApproved: { background: "#ecfdf3", borderColor: "#a7f3d0", color: "#067647" },
  pillRejected: { background: "#fef2f2", borderColor: "#fecaca", color: "#b42318" },

  actions: { display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" },

  approveBtn: {
    height: 38,
    padding: "0 14px",
    borderRadius: 12,
    border: 0,
    color: "#fff",
    cursor: "pointer",
    fontWeight: 950,
    background: "linear-gradient(135deg,#10b981,#34d399)",
    boxShadow: "0 12px 24px rgba(16,185,129,0.16)",
  },

  rejectBtn: {
    height: 38,
    padding: "0 14px",
    borderRadius: 12,
    border: 0,
    color: "#fff",
    cursor: "pointer",
    fontWeight: 950,
    background: "linear-gradient(135deg,#ef4444,#f97316)",
    boxShadow: "0 12px 24px rgba(239,68,68,0.16)",
  },

  // ✅ NEW: delete single button
  deleteBtn: {
    height: 38,
    padding: "0 14px",
    borderRadius: 12,
    border: 0,
    color: "#fff",
    cursor: "pointer",
    fontWeight: 950,
    background: "#b42318",
    boxShadow: "0 12px 24px rgba(180,35,24,0.16)",
  },

  mediaBtn: {
    height: 36,
    padding: "0 12px",
    borderRadius: 12,
    border: "1px solid #e5e7eb",
    background: "#fff",
    cursor: "pointer",
    fontWeight: 950,
    color: "#0b1220",
  },

  mobileList: { marginTop: 12, display: "grid", gap: 12 },
  mobileItem: {
    border: "1px solid #eef2f7",
    borderRadius: 18,
    padding: 12,
    background: "#fff",
    boxShadow: "0 16px 30px rgba(16,24,40,0.06)",
  },
  mobileTop: { display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10 },
  mobileBody: { marginTop: 10, display: "grid", gap: 10 },
  mobileMetaRow: { display: "flex", justifyContent: "space-between", gap: 10, alignItems: "center", flexWrap: "wrap" },
  mobileActions: { display: "grid", gap: 10 },

  modalOverlay: {
    position: "fixed",
    inset: 0,
    background: "rgba(11,18,32,0.45)",
    zIndex: 9999,
    display: "grid",
    placeItems: "center",
    padding: 14,
  },
  modal: {
    width: "920px",
    maxWidth: "96vw",
    background: "#fff",
    borderRadius: 18,
    border: "1px solid #eef2f7",
    boxShadow: "0 22px 60px rgba(16,24,40,0.22)",
    overflow: "hidden",
  },
  modalHeader: {
    padding: 12,
    borderBottom: "1px solid #eef2f7",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 10,
    background: "linear-gradient(180deg,#ffffff,#fbfdff)",
  },
  modalTitle: { fontSize: 16, fontWeight: 1000, color: "#0b1220" },
  modalClose: {
    height: 36,
    width: 36,
    borderRadius: 12,
    border: "1px solid #e5e7eb",
    background: "#fff",
    cursor: "pointer",
    fontWeight: 950,
    color: "#0b1220",
  },
  modalBody: { padding: 12 },

  mediaGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 12 },

  thumb: {
    border: "1px solid #eef2f7",
    borderRadius: 16,
    overflow: "hidden",
    background: "#fff",
    display: "block",
    textDecoration: "none",
  },

  thumbImg: { width: "100%", height: 180, objectFit: "cover", display: "block" },
  thumbVideo: { width: "100%", height: 180, objectFit: "cover", display: "block" },
  thumbAudio: { width: "100%", padding: 12, display: "block" },

  thumbFile: {
    display: "grid",
    placeItems: "center",
    height: 180,
    borderRadius: 16,
    border: "1px dashed #cbd5e1",
    background: "#f8fafc",
    fontWeight: 950,
    color: "#0b1220",
    textDecoration: "none",
  },

  pager: {
    marginTop: 14,
    paddingTop: 14,
    borderTop: "1px solid #eef2f7",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 10,
    flexWrap: "wrap",
  },
  pagerLeft: { display: "flex", gap: 10 },
  pagerCenter: { display: "flex", justifyContent: "center" },
  pagerRight: { display: "flex", justifyContent: "flex-end" },
  pagerBtn: {
    height: 34,
    padding: "0 12px",
    borderRadius: 12,
    border: "1px solid #e5e7eb",
    background: "#fff",
    cursor: "pointer",
    fontWeight: 950,
    color: "#0b1220",
  },
  pagerBtnDisabled: { opacity: 0.45, cursor: "not-allowed" },
  pagerSelect: {
    height: 34,
    borderRadius: 12,
    border: "1px solid #e5e7eb",
    padding: "0 10px",
    fontWeight: 900,
    background: "#fff",
    outline: "none",
  },
};
